import fileApi from '@/apis/file';
import { getResizedImage } from '@/lib/imageHelper';
import { extension as convertMimeTypeToExtension, lookup as lookupMimeTypes } from 'mime-types';

export const MAX_SAVABLE_FILES_COUNT = 3;

export interface FileObj {
  id: number;
  content: string;
  type: string;
  name: string;
  file: File;
}
export const FILE_TYPE_UNKNOWN = 'unknown';

export async function fetchFileAsObjectInfo(filePath: string): Promise<{
  url: string | null;
  type: string | null;
} | null> {
  if (!filePath) {
    return null;
  }
  const { data: blob } = await fileApi.getFile({path: filePath});
  return {
    url: URL.createObjectURL(blob),
    type: blob.type,
  };
}

export async function convertFilesToFileObjs(fileList: FileList, maxId: number, limitMB: number): Promise<FileObj[]> {
  if (fileList.length === 0) { return []; }
  const fileObjs: FileObj[] = [];

  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];
    if (file.type.includes('image/')) {
      await addImageFileObj(fileObjs, file, maxId + i + 1);
    } else {
      checkFileSize(file, limitMB);
      addGeneralFileObj(fileObjs, file, maxId + i + 1);
    }
  }
  return fileObjs;
}

async function addImageFileObj(fileObjs: FileObj[], file: File, id: number): Promise<void> {
  // リサイズ後は確実に10MBを切るのでサイズ確認はしなくてよい.
  const resizedImage = await getResizedImage(file, { maxWidth: 1000, maxHeight: 1000 });
  if (!resizedImage) return;
  fileObjs.push({
    id,
    content: URL.createObjectURL(resizedImage),
    type: file.type,
    name: file.name,
    file: new File([resizedImage], file.name, { type: file.type }),
  });
}

function addGeneralFileObj(fileObjs: FileObj[], file: File, id: number): void {
  fileObjs.push({
    id,
    content: URL.createObjectURL(file),
    type: file.type,
    name: file.name,
    file,
  });
}

function checkFileSize(file: File, limitMB: number): void {
  const fileSize = file.size;
  const limitSize = limitMB * 1024 * 1024;
  if (fileSize > limitSize) {
    throw new Error(`添付ファイルの最大サイズは${limitMB}MBです。`);
  }
}

export const fileTypeIconFromFilePath = (filePath: string): string => {
  const mimeType = lookupMimeTypes(filePath);
  if (!mimeType) return 'fa-file';
  const extension = convertMimeTypeToExtension(mimeType);
  if (!extension) return 'fa-file';
  if (extension === 'pdf') return 'fa-file-pdf';
  if (extension === 'doc' || extension === 'docx') return 'fa-file-word';
  if (extension === 'xls' || extension === 'xlsx') return 'fa-file-excel';
  if (extension === 'ppt' || extension === 'pptx') return 'fa-file-powerpoint';
  return 'fa-file';
};

export const isImageMimeType = (mimeType: string): boolean => {
  return mimeType.startsWith('image/');
};

export function isImageFileType(filePath: string): boolean {
  const mimeType = lookupMimeTypes(filePath);
  if (!mimeType) return false;
  const extension = convertMimeTypeToExtension(mimeType);
  if (!extension) return false;
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension.toLowerCase());
}
